import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import { StatusChip } from "@/components/StatusChip";
import { Loader2, Save, Send, Info } from "lucide-react";
import { toast } from "sonner";

const enhVal = (f) => (f && typeof f === "object" ? f.submitted || "" : f || "");

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-border bg-card p-4 space-y-4">
    <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
    {children}
  </div>
);

export default function AdvertiserContentProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("draft");
  const [meta, setMeta] = useState({});
  const [f, setF] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/advertiser/content-profile");
      const p = data.profile || {};
      setStatus(p.status || "draft");
      setMeta({ rejection_reason: p.rejection_reason, change_request_note: p.change_request_note });
      setF({
        business_name: p.business_name || "", category: p.category || "", website: p.website || "",
        address: p.address || "", city: p.city || "", state: p.state || "", zip: p.zip || "",
        phone: p.phone || "", email: p.email || "",
        short_description: enhVal(p.short_description), full_description: enhVal(p.full_description),
        amenities: enhVal(p.amenities), cyclist_features: enhVal(p.cyclist_features),
        special_offers: enhVal(p.special_offers), coupon_info: enhVal(p.coupon_info),
        facebook: p.facebook || "", instagram: p.instagram || "", youtube: p.youtube || "", tiktok: p.tiktok || "",
        logo: p.logo || null, cover_image: p.cover_image || null, gallery: p.gallery || [],
      });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/advertiser/content-profile", f); setStatus(data.profile.status); toast.success("Profile saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put("/advertiser/content-profile", f);
      const { data } = await api.post("/advertiser/content-profile/submit");
      setStatus(data.profile.status); setMeta({});
      toast.success("Submitted for review");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (loading || !f) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="advertiser-content-profile">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold">Public Listing</h2>
          <p className="text-sm text-muted-foreground">How your business appears to riders. Submit for review to publish changes.</p>
        </div>
        <StatusChip status={status} testId="advcp-status" />
      </div>

      {status === "changes_requested" && meta.change_request_note && (
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-sm text-orange-300 flex gap-2" data-testid="advcp-changes-note"><Info className="w-4 h-4 mt-0.5 shrink-0" />Changes requested: {meta.change_request_note}</div>
      )}
      {status === "rejected" && meta.rejection_reason && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 flex gap-2" data-testid="advcp-reject-note"><Info className="w-4 h-4 mt-0.5 shrink-0" />Rejected: {meta.rejection_reason}</div>
      )}

      <Section title="Business Info">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Business name</Label><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="advcp-business_name-input" /></div>
          <div><Label>Category</Label><Input value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="Cafe, Restaurant, Hotel…" data-testid="advcp-category-input" /></div>
          <div><Label>Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="advcp-website-input" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="advcp-phone-input" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="advcp-email-input" /></div>
          <div><Label>Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="advcp-address-input" /></div>
          <div><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="advcp-city-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>State</Label><Input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="advcp-state-input" /></div>
            <div><Label>ZIP</Label><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} data-testid="advcp-zip-input" /></div>
          </div>
        </div>
      </Section>

      <Section title="Descriptions">
        <div><Label>Short description</Label><Textarea rows={2} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="One-liner for cards" data-testid="advcp-short_description-input" /></div>
        <div><Label>Full description</Label><Textarea rows={4} value={f.full_description} onChange={(e) => set("full_description", e.target.value)} data-testid="advcp-full_description-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Amenities</Label><Textarea rows={3} value={f.amenities} onChange={(e) => set("amenities", e.target.value)} data-testid="advcp-amenities-input" /></div>
          <div><Label>Cyclist features</Label><Textarea rows={3} value={f.cyclist_features} onChange={(e) => set("cyclist_features", e.target.value)} placeholder="Bike parking, repair station, water refill…" data-testid="advcp-cyclist_features-input" /></div>
        </div>
      </Section>

      <Section title="Offers">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Special offers</Label><Textarea rows={3} value={f.special_offers} onChange={(e) => set("special_offers", e.target.value)} data-testid="advcp-special_offers-input" /></div>
          <div><Label>Coupon info</Label><Textarea rows={3} value={f.coupon_info} onChange={(e) => set("coupon_info", e.target.value)} data-testid="advcp-coupon_info-input" /></div>
        </div>
      </Section>

      <Section title="Media">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>Logo</Label><PhotoUpload photos={f.logo ? [f.logo] : []} onChange={(ids) => set("logo", ids[0] || null)} multiple={false} label="Logo" testId="advcp-logo" /></div>
          <div><Label>Cover image</Label><PhotoUpload photos={f.cover_image ? [f.cover_image] : []} onChange={(ids) => set("cover_image", ids[0] || null)} multiple={false} label="Cover" testId="advcp-cover" /></div>
        </div>
        <div><Label>Gallery</Label><PhotoUpload photos={f.gallery} onChange={(ids) => set("gallery", ids)} multiple label="Add" testId="advcp-gallery" /></div>
      </Section>

      <Section title="Social Links">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Facebook</Label><Input value={f.facebook} onChange={(e) => set("facebook", e.target.value)} data-testid="advcp-facebook-input" /></div>
          <div><Label>Instagram</Label><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="advcp-instagram-input" /></div>
          <div><Label>YouTube</Label><Input value={f.youtube} onChange={(e) => set("youtube", e.target.value)} data-testid="advcp-youtube-input" /></div>
          <div><Label>TikTok</Label><Input value={f.tiktok} onChange={(e) => set("tiktok", e.target.value)} data-testid="advcp-tiktok-input" /></div>
        </div>
      </Section>

      <div className="flex justify-end gap-2 sticky bottom-0 py-3 bg-background/80 backdrop-blur">
        <Button variant="outline" onClick={save} disabled={saving || submitting} data-testid="advcp-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save draft</Button>
        <Button onClick={submit} disabled={saving || submitting} data-testid="advcp-submit">{submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Submit for review</Button>
      </div>
    </div>
  );
}
